import { z } from "zod";

/** Categories reachable through the activity digest: each has at least one email type. */
export const NOTIFICATION_ACTIVITY_EMAIL_CATEGORIES = ["deals", "social"] as const;

export const notificationActivityEmailCategorySchema = z.enum(
  NOTIFICATION_ACTIVITY_EMAIL_CATEGORIES,
);
export type NotificationActivityEmailCategory = z.infer<
  typeof notificationActivityEmailCategorySchema
>;

/**
 * `product` covers feature and account messages we send on our own initiative rather
 * than in response to catalog or social activity. It is the one category with no
 * `NOTIFICATION_EMAIL_TYPES` entry, because those emails are delivered directly instead
 * of being rolled into the activity digest — but it is still a preference a member can
 * turn off, and unsubscribe tokens are issued for it like any other.
 */
export const NOTIFICATION_EMAIL_CATEGORIES = [
  ...NOTIFICATION_ACTIVITY_EMAIL_CATEGORIES,
  "product",
] as const;

export const notificationEmailCategorySchema = z.enum(NOTIFICATION_EMAIL_CATEGORIES);
export type NotificationEmailCategory = z.infer<typeof notificationEmailCategorySchema>;

export const NOTIFICATION_EMAIL_TYPES = [
  "deal_price_drop",
  "deal_best_deal",
  "new_follower",
  "post_like",
  "feed_event_comment",
] as const;

export const notificationEmailTypeSchema = z.enum(NOTIFICATION_EMAIL_TYPES);
export type NotificationEmailType = z.infer<typeof notificationEmailTypeSchema>;

export const NOTIFICATION_EMAIL_TYPE_CATEGORY: Record<
  NotificationEmailType,
  NotificationActivityEmailCategory
> = {
  deal_price_drop: "deals",
  deal_best_deal: "deals",
  new_follower: "social",
  post_like: "social",
  feed_event_comment: "social",
};

export function notificationEmailCategoryForType(
  type: string,
): NotificationActivityEmailCategory | null {
  const parsed = notificationEmailTypeSchema.safeParse(type);
  if (!parsed.success) return null;
  return NOTIFICATION_EMAIL_TYPE_CATEGORY[parsed.data];
}

export const DEAL_PRICE_INDICATORS = [
  "observed_historical_low",
  "observed_historical_high",
  "all_time_low",
  "all_time_high",
  "below_average",
  "below_typical",
  "above_average",
  "above_typical",
] as const;

export const dealPriceIndicatorSchema = z.enum(DEAL_PRICE_INDICATORS);
export type DealPriceIndicator = z.infer<typeof dealPriceIndicatorSchema>;

export const DEAL_PRICE_INDICATOR_PRIORITY: DealPriceIndicator[] = [
  "observed_historical_low",
  "observed_historical_high",
  "all_time_low",
  "all_time_high",
  "below_typical",
  "below_average",
  "above_typical",
  "above_average",
];

export function displayDealPriceIndicators(
  indicators: string[] | null | undefined,
): DealPriceIndicator[] {
  const present = new Set(
    (indicators ?? []).filter(
      (indicator): indicator is DealPriceIndicator =>
        dealPriceIndicatorSchema.safeParse(indicator).success,
    ),
  );

  const indicator = DEAL_PRICE_INDICATOR_PRIORITY.find((candidate) => present.has(candidate));
  return indicator ? [indicator] : [];
}
